import React, { useEffect, useRef } from 'react';
import { X } from 'lucide-react';
import { formatDate, formatAddress, truncateText } from '../../utils/helpers';
import { getDisplayName } from '../../lib/utils';
import LeadTasksPanel from '../tasks/LeadTasksPanel';
import FlashPill from '../propertyDetails/FlashPill';
import { Button } from '../ui/button';
import { useNavigate } from 'react-router-dom';
import { convertLeadToClient } from '../../features/clients/api/convertLeadToClient';
import { toast } from 'sonner';

const LeadInfoPopover = ({ lead, onClose, position = 'right', variant = 'overlay' }) => {
  const popoverRef = useRef(null);
  const navigate = useNavigate();

  const handleConvertToClient = async () => {
    if (!lead?.id) return;

    const res = await convertLeadToClient(lead.id);

    if (!res.success) {
      toast.error(res.message || 'Conversion failed');
      return;
    }

    toast.success('Lead converted to client');
    onClose?.();
    navigate(`/clients/${res.clientId || lead.id}`);
  };

  // Inline variant: small, card-style popover with click-outside-to-close
  useEffect(() => {
    if (variant !== 'inline') return;

    const handleClickOutside = (event) => {
      if (popoverRef.current && !popoverRef.current.contains(event.target)) {
        onClose?.();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [variant, onClose]);

  if (variant === 'inline') {
    return (
      <div
        ref={popoverRef}
        className="relative z-20"
        onMouseDown={(e) => e.stopPropagation()} // prevent interfering with drag-and-drop
      >
        <div className="w-60 bg-white rounded-lg shadow-lg border border-gray-200 p-3 text-xs space-y-3">
          {/* Name */}
          <div>
            <p className="font-semibold text-[var(--cb-navy)] text-sm">
              {getDisplayName(lead, 'Unnamed Lead')}
            </p>
          </div>

          {/* Flash — bright property warning at top of inline popover */}
          {lead.propertyDetails?.flashNote && (
            <FlashPill
              propertyDetails={lead.propertyDetails}
              size="sm"
              testId="popover-inline-flash"
            />
          )}

          {/* Address */}
          <div>
            <p className="text-xs text-gray-700">
              {formatAddress(lead.address)}
            </p>
          </div>

          {/* Service */}
          {lead.service && (
            <div>
              <span
                className="inline-block px-2 py-0.5 text-[10px] rounded"
                style={{
                  backgroundColor: 'rgba(242, 107, 29, 0.1)',
                  color: 'var(--cb-orange)'
                }}
              >
                {lead.service}
              </span>
            </div>
          )}

          {/* Dates */}
          <div className="flex justify-between text-[10px] text-gray-500">
            <span>Created: {formatDate(lead.createdAt)}</span>
            <span>Last: {formatDate(lead.lastContact)}</span>
          </div>

          {/* Notes (truncated) */}
          {lead.notes && (
            <div>
              <p className="text-xs text-gray-700">
                {truncateText(lead.notes, 120)}
                {lead.notes.length > 120 && (
                  <span className="ml-1 text-[10px] text-[var(--cb-orange)]">View more…</span>
                )}
              </p>
            </div>
          )}

          {/* Tasks (add-only) */}
          <div className="pt-2 border-t border-gray-100">
            <LeadTasksPanel leadId={lead.id} compact showCompleted={false} showExistingTasks={false} />
          </div>
        </div>
      </div>
    );
  }

  // Default overlay variant (full details, with backdrop)
  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-40"
        onClick={onClose}
      />
      
      {/* Popover */}
      <div
        className="absolute z-50 w-80 bg-white rounded-lg shadow-xl border border-gray-200"
        style={{
          [position === 'right' ? 'left' : 'right']: '100%',
          top: 0,
          marginLeft: position === 'right' ? '8px' : '0',
          marginRight: position === 'left' ? '8px' : '0',
        }}
        ref={popoverRef}
        onMouseDown={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold" style={{ color: 'var(--cb-navy)' }}>
            Lead Details
          </h3>
          <div className="flex items-center gap-2">
            <Button
              variant="default"
              size="sm"
              onClick={handleConvertToClient}
            >
              Convert to Client
            </Button>
            <button
              onClick={onClose}
              className="p-1 hover:bg-gray-100 rounded transition-colors"
            >
              <X className="h-4 w-4 text-gray-500" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-4 space-y-4 max-h-96 overflow-y-auto">
          {/* Flash — bright property warning at top of overlay popover */}
          {lead.propertyDetails?.flashNote && (
            <FlashPill
              propertyDetails={lead.propertyDetails}
              testId="popover-overlay-flash"
            />
          )}

          {/* Name */}
          <div>
            <label className="text-xs font-semibold text-gray-500 uppercase">Name</label>
            <p className="text-sm text-gray-900">{getDisplayName(lead, 'N/A')}</p>
          </div>

          {/* Address */}
          <div>
            <label className="text-xs font-semibold text-gray-500 uppercase">Address</label>
            <p className="text-sm text-gray-900">
              {formatAddress(lead.address)}
            </p>
          </div>

          {/* Email */}
          {lead.email && (
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase">Email</label>
              <p className="text-sm text-gray-900 break-all">{lead.email}</p>
            </div>
          )}

          {/* Phone */}
          {lead.phone && (
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase">Phone</label>
              <p className="text-sm text-gray-900">{lead.phone}</p>
            </div>
          )}

          {/* Service */}
          {lead.service && (
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase">Service</label>
              <p className="text-sm text-gray-900">{lead.service}</p>
            </div>
          )}

          {/* Notes */}
          {lead.notes && (
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase">Notes</label>
              <p className="text-sm text-gray-900 whitespace-pre-wrap">{lead.notes}</p>
            </div>
          )}

          {/* Created At */}
          <div>
            <label className="text-xs font-semibold text-gray-500 uppercase">Created</label>
            <p className="text-sm text-gray-900">{formatDate(lead.createdAt)}</p>
          </div>

          {/* Last Contact */}
          <div>
            <label className="text-xs font-semibold text-gray-500 uppercase">Last Contact</label>
            <p className="text-sm text-gray-900">{formatDate(lead.lastContact)}</p>
          </div>

          {/* Stage */}
          {lead.stage && (
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase">Stage</label>
              <p className="text-sm text-gray-900">{lead.stage}</p>
            </div>
          )}

          {/* Tasks accordion (add-only) */}
          <div className="pt-3 border-t border-gray-200">
            <LeadTasksPanel leadId={lead.id} showCompleted={false} showExistingTasks={false} />
          </div>
        </div>
      </div>
    </>
  );
};

export default LeadInfoPopover;
